// What a newly created slide starts with.
//
// A slide in this app is not an object — it is a run of blocks between two
// slide-break dividers (see slideSegmentation.js). "Creating a slide" is
// therefore "inserting a divider, and then whatever should sit after it", and
// that second half is what this module owns.
//
// It exists because that decision used to be made in two places at once —
// `handleAddSlideAfter` and `handleAddSlideFromDrawer` each built their own
// `{ type: 'text', content: 'Click to edit this slide content.' }` inline — so
// "a new slide" meant "a slide with a text box on it" everywhere, with no way
// to ask for anything else and no single line to change.
//
// **Why the text box was there.** Not decoration: an empty slide is
// `isEmptySlide`, and the export builder drops empty slides. A slide created
// with nothing on it and left that way does not reach the PowerPoint or the
// PDF, so seeding a text block made every new slide "real" immediately. That
// is a fair default for a slide you are about to type on, and quite wrong for
// one you are about to put a picture or a Custom HTML/CSS layout on — you have
// to delete the text box first, and an author who does not notice it exports a
// stray "Click to edit this slide content." Hence two named variants rather
// than one silent default.

/** The starter copy a Text Slide carries. One definition, one place. */
export const SLIDE_STARTER_TEXT = 'Click to edit this slide content.';

/**
 * The kinds of slide the app can create from scratch.
 *
 * These are command vocabulary, not persisted data: a slide records only its
 * blocks, so a deck saved today has no idea which variant made it and nothing
 * has to migrate.
 */
export const SLIDE_VARIANTS = {
  /** No blocks at all — the slide, and nothing on it. */
  BLANK: 'blank',
  /** One text block holding the starter copy, ready to type over. */
  TEXT: 'text'
};

/**
 * The blocks that follow the slide-break divider for a new slide.
 *
 * @param {object} options
 * @param {string} options.slideId - the id the divider was created with; every
 *   block on a slide carries it, which is what binds them to that slide
 * @param {string} [options.variant] - one of `SLIDE_VARIANTS`; anything
 *   unrecognized is treated as blank, because inventing content for a command
 *   nobody wrote is the failure this module exists to prevent
 * @param {() => string} options.generateId
 * @returns {Array<object>} possibly empty — an empty list is the whole point
 *   of the blank variant, and callers must splice it in as a list rather than
 *   assuming one block came back
 */
export const createSlideStarterBlocks = ({ slideId, variant = SLIDE_VARIANTS.BLANK, generateId }) => {
  if (variant !== SLIDE_VARIANTS.TEXT) return [];
  return [{
    id: generateId(),
    type: 'text',
    content: SLIDE_STARTER_TEXT,
    slideId
  }];
};
